import get from 'lodash/get';
import {GetterTree} from 'vuex';
import {
    BriefFreelancerJobTitle,
    BriefFreelancerProfile,
    BriefFreelancerSkills,
    BriefIdentity,
    BriefMissionDescription,
    BriefMissionTitle,
    BriefState,
    BriefWhenConditions,
    BriefWhereConditions,
} from '@/brief-hub/store/modules/brief/types';
import BriefResource from '@/brief-hub/brief/BriefResource';
import {
    BriefApiExperienceLevel,
    DateAsString,
    FreelancerAvailability,
    LanguageResource,
    ProjectDuration,
    StartDateFlexibility,
    StartDateType,
} from '@/common/api/api-raw-types';
import {StoreState} from '@/brief-hub/store/state/StoreState';

export const getters: GetterTree<BriefState, StoreState> = {
    brief(state: BriefState): BriefResource | null {
        return state.brief;
    },

    loading(state: BriefState): boolean {
        return state.loading;
    },

    error(state: BriefState): boolean {
        return state.error;
    },

    saved(state: BriefState): boolean {
        return state.savedNotification;
    },

    informationModalDisplayed(state: BriefState): boolean {
        return state.informationModalDisplayed;
    },

    briefFunnelId(state: BriefState): string {
        return state.briefFunnelId;
    },

    originReferrer(state: BriefState): string | null {
        return state.originReferrer;
    },

    briefFreelancerProfile(state: BriefState): BriefFreelancerProfile {
        const selectedCategory: string | null =
            get(state.briefForm.profileDetails, 'freelancerCategories[0]') || get(state.brief, 'profileDetails.freelancerCategories[0]');
        const selectedCategoryLabel: string | null = get(state.brief, 'profileDetails.freelancerCategoryLabels[0]');
        const selectedFamily: string | null = get(state.brief, 'profileDetails.freelancerRequiredCategories[0].familyId');
        const selectedFamilyLabel: string | null = get(state.brief, 'profileDetails.freelancerRequiredCategories[0].label');
        const selectedExperienceLevel: BriefApiExperienceLevel | null =
            state.briefForm.profileDetails?.experienceLevel || state.brief?.profileDetails?.experienceLevel || null;

        return {selectedCategory, selectedFamily, selectedCategoryLabel, selectedExperienceLevel, selectedFamilyLabel};
    },

    briefFreelancerJobTitle(state: BriefState): {jobTitle: BriefFreelancerJobTitle | string | null} {
        const jobTitle: BriefFreelancerJobTitle | string | null =
            state.briefForm.jobTitle?.jobTitle || state.brief?.profileDetails?.freelancerJobTitle || null;
        return {jobTitle};
    },

    briefFreelancerSkills(state: BriefState): BriefFreelancerSkills {
        const selectedSkills: string[] = state.brief?.profileDetails.freelancerSkills || [];
        const selectedLanguages: LanguageResource[] = state.brief?.profileDetails?.languages || [];

        if (state.briefForm.skills !== null) {
            return {
                selectedSkills: get(state.briefForm.skills, 'selectedSkills'),
                selectedLanguages: state.briefForm.skills.selectedLanguages,
            };
        }
        return {selectedSkills, selectedLanguages};
    },

    briefWhenConditions(state: BriefState): BriefWhenConditions {
        const projectDuration: ProjectDuration | null = get(state.brief, 'briefDetails.projectDuration');
        const startDate: DateAsString | null = get(state.brief, 'briefDetails.startDate');
        const startDateType: StartDateType | null = get(state.brief, 'briefDetails.startDateType');
        const startDateFlexibility: StartDateFlexibility | null = get(state.brief, 'briefDetails.startDateFlexibility');
        const freelancerAvailability: FreelancerAvailability | null = get(state.brief, 'briefDetails.freelancerAvailability');
        const budget: number | null = get(state.brief, 'briefDetails.budget');
        const dailyFreelancerRate: number | null = get(state.brief, 'briefDetails.dailyFreelancerRate');
        const communicateRate: boolean | null = get(state.brief, 'briefDetails.communicateRate');

        if (state.briefForm.whenConditions !== null) {
            return {
                budget: get(state.briefForm.whenConditions, 'budget'),
                startDate: get(state.briefForm.whenConditions, 'startDate'),
                startDateType: get(state.briefForm.whenConditions, 'startDateType'),
                startDateFlexibility: get(state.briefForm.whenConditions, 'startDateFlexibility'),
                projectDuration: get(state.briefForm.whenConditions, 'projectDuration'),
                freelancerAvailability: get(state.briefForm.whenConditions, 'freelancerAvailability'),
                dailyFreelancerRate: get(state.briefForm.whenConditions, 'dailyFreelancerRate'),
                communicateRate: get(state.briefForm.whenConditions, 'communicateRate'),
            };
        }
        return {
            projectDuration,
            startDate,
            startDateType,
            startDateFlexibility,
            freelancerAvailability,
            budget,
            dailyFreelancerRate,
            communicateRate,
        };
    },

    briefWhereConditions({briefForm, brief}: BriefState): BriefWhereConditions {
        if (briefForm.whereConditions) {
            return {
                // true by default
                remote: briefForm.whereConditions.remote !== false,
                remoteFrequency: briefForm.whereConditions.remoteFrequency || null,
                address: briefForm.whereConditions.address || null,
                workingStatus: briefForm.whereConditions.workingStatus || null,
            };
        }

        return {
            // true by default
            remote: brief?.briefDetails.remote !== false,
            remoteFrequency: brief?.briefDetails.remoteFrequency || null,
            address: brief?.briefDetails.address || null,
            workingStatus: brief?.briefDetails.workingStatus || null,
        };
    },

    briefMissionTitle(state: BriefState): BriefMissionTitle {
        if (state.briefForm.missionTitle !== null) {
            return {
                title: state.briefForm.missionTitle.title,
            };
        }
        const title: string | null = get(state.brief, 'briefDetails.title');
        return {title};
    },

    briefMissionDescription(state: BriefState): BriefMissionDescription {
        if (state.briefForm.missionDescription !== null) {
            return {
                description: state.briefForm.missionDescription.description,
            };
        }
        const description: string | null = get(state.brief, 'briefDetails.description');
        return {description};
    },

    briefIdentity(state: BriefState): BriefIdentity | null {
        if (state.briefForm.identity !== null) {
            return {
                identityId: state.briefForm.identity.identityId,
            };
        }

        const identityId: string | null = state.brief !== null ? state.brief.identityId : null;
        return {identityId};
    },

    isFormValid(state: BriefState): boolean {
        return state.payload !== null;
    },
};
